/**
 * MorphologyOperations - Morphological operations on voxel grids
 * 
 * Implements dilation, erosion, opening, closing for SDF and binary volumes.
 */

import { VoxelGrid } from './VoxelGrid';

// 6-connected neighborhood (face neighbors)
const NEIGHBORS_6: Array<[number, number, number]> = [
  [-1, 0, 0], [1, 0, 0],
  [0, -1, 0], [0, 1, 0],
  [0, 0, -1], [0, 0, 1],
];

// 26-connected neighborhood (all neighbors including corners)
const NEIGHBORS_26: Array<[number, number, number]> = [];
for (let dx = -1; dx <= 1; dx++) {
  for (let dy = -1; dy <= 1; dy++) {
    for (let dz = -1; dz <= 1; dz++) {
      if (dx !== 0 || dy !== 0 || dz !== 0) {
        NEIGHBORS_26.push([dx, dy, dz]);
      }
    }
  }
}

export type KernelType = '6-connected' | '26-connected';

function getNeighbors(kernel: KernelType): Array<[number, number, number]> {
  return kernel === '6-connected' ? NEIGHBORS_6 : NEIGHBORS_26;
}

/**
 * Sliding-window extreme (van Herk–Gil-Werman) over one grid axis, radius `r`:
 * out[i] = min/max of the line values in [i−r, i+r] ∩ [0, L). O(1) amortized per
 * element regardless of r (block prefix/suffix scans); the first/last r elements
 * fall back to a direct scan, where the block identity does not hold exactly.
 * Operates in place on `data` (each line is buffered before writing).
 */
function slidingExtremeAxis(
  data: Float32Array,
  lineCount: number,
  lineStart: (line: number) => number,
  stride: number,
  length: number,
  radius: number,
  useMin: boolean,
): void {
  const W = 2 * radius + 1;
  const line = new Float32Array(length);
  const prefix = new Float32Array(length);
  const suffix = new Float32Array(length);

  for (let l = 0; l < lineCount; l++) {
    const base = lineStart(l);
    for (let i = 0, p = base; i < length; i++, p += stride) line[i] = data[p];

    if (length <= 2 * radius) {
      // Short line: every window is edge-clamped — direct scan.
      for (let i = 0, p = base; i < length; i++, p += stride) {
        const lo = Math.max(0, i - radius);
        const hi = Math.min(length - 1, i + radius);
        let ext = line[lo];
        for (let k = lo + 1; k <= hi; k++) {
          ext = useMin ? Math.min(ext, line[k]) : Math.max(ext, line[k]);
        }
        data[p] = ext;
      }
      continue;
    }

    // Block prefix/suffix extremes (blocks of W aligned to index 0).
    for (let i = 0; i < length; i++) {
      const v = line[i];
      prefix[i] =
        i % W === 0 ? v : useMin ? Math.min(prefix[i - 1], v) : Math.max(prefix[i - 1], v);
    }
    for (let i = length - 1; i >= 0; i--) {
      const v = line[i];
      suffix[i] =
        i % W === W - 1 || i === length - 1
          ? v
          : useMin
            ? Math.min(suffix[i + 1], v)
            : Math.max(suffix[i + 1], v);
    }

    let p = base;
    for (let i = 0; i < radius; i++, p += stride) {
      // Left edge: window [0, i+r] lies within block 0 → prefix alone is exact.
      data[p] = prefix[i + radius];
    }
    for (let i = radius; i < length - radius; i++, p += stride) {
      const a = suffix[i - radius];
      const b = prefix[i + radius];
      data[p] = useMin ? Math.min(a, b) : Math.max(a, b);
    }
    for (let i = Math.max(radius, length - radius); i < length; i++, p += stride) {
      // Right edge: block-aligned prefix can reach before the window — direct scan.
      const lo = i - radius;
      let ext = line[lo];
      for (let k = lo + 1; k < length; k++) {
        ext = useMin ? Math.min(ext, line[k]) : Math.max(ext, line[k]);
      }
      data[p] = ext;
    }
  }
}

/**
 * Chebyshev-ball (26-connected, radius N) min/max filter as three separable axis
 * passes + one uniform value shift. Produces EXACTLY the same field as N iterations
 * of the 26-neighbour min/max loop with a per-iteration ±cellSize shift (the shift is
 * uniform, so it commutes with min/max), at O(total) instead of O(total · 27 · N).
 * Operates in place — callers that need the input intact clone first.
 */
function boxExtremeSDFInPlace(grid: VoxelGrid, radius: number, useMin: boolean): void {
  const { counts, cellSize, strides } = grid.info;
  const data = grid.data;

  // Lines along z (contiguous), then y, then x.
  slidingExtremeAxis(
    data,
    counts.x * counts.y,
    (l) => l * counts.z,
    1,
    counts.z,
    radius,
    useMin,
  );
  slidingExtremeAxis(
    data,
    counts.x * counts.z,
    (l) => Math.floor(l / counts.z) * strides.x + (l % counts.z),
    strides.y,
    counts.y,
    radius,
    useMin,
  );
  slidingExtremeAxis(
    data,
    counts.y * counts.z,
    (l) => l,
    strides.x,
    counts.x,
    radius,
    useMin,
  );

  const shift = radius * cellSize;
  for (let i = 0; i < data.length; i++) data[i] += useMin ? -shift : shift;
}

function boxExtremeSDF(grid: VoxelGrid, radius: number, useMin: boolean): VoxelGrid {
  const result = grid.clone();
  boxExtremeSDFInPlace(result, radius, useMin);
  return result;
}

/**
 * Dilate SDF (expand volume, make more negative)
 * Uses min-filter and subtracts cell size per iteration
 */
export function dilateSDF(
  grid: VoxelGrid,
  iterations: number,
  kernel: KernelType = '6-connected',
): VoxelGrid {
  if (iterations <= 0) return grid.clone();
  // 26-connected N-step dilation == Chebyshev-ball box filter → separable fast path.
  if (kernel === '26-connected') return boxExtremeSDF(grid, iterations, true);
  const neighbors = getNeighbors(kernel);
  const { counts, cellSize } = grid.info;
  let current = grid.clone();

  for (let iter = 0; iter < iterations; iter++) {
    const next = new VoxelGrid(current.info);

    for (let x = 0; x < counts.x; x++) {
      for (let y = 0; y < counts.y; y++) {
        for (let z = 0; z < counts.z; z++) {
          const idx = current.index(x, y, z);
          let minVal = current.data[idx];

          for (const [dx, dy, dz] of neighbors) {
            const nx = x + dx, ny = y + dy, nz = z + dz;
            if (!current.inBounds(nx, ny, nz)) continue;
            const nidx = current.index(nx, ny, nz);
            minVal = Math.min(minVal, current.data[nidx]);
          }

          // Subtract cell size (expand inward = more negative)
          next.data[idx] = minVal - cellSize;
        }
      }
    }

    current = next;
  }

  return current;
}

/**
 * Erode SDF (shrink volume, make more positive)
 * Uses max-filter and adds cell size per iteration
 */
export function erodeSDF(
  grid: VoxelGrid,
  iterations: number,
  kernel: KernelType = '6-connected',
): VoxelGrid {
  if (iterations <= 0) return grid.clone();
  // 26-connected N-step erosion == Chebyshev-ball box filter → separable fast path.
  if (kernel === '26-connected') return boxExtremeSDF(grid, iterations, false);
  const neighbors = getNeighbors(kernel);
  const { counts, cellSize } = grid.info;
  let current = grid.clone();

  for (let iter = 0; iter < iterations; iter++) {
    const next = new VoxelGrid(current.info);

    for (let x = 0; x < counts.x; x++) {
      for (let y = 0; y < counts.y; y++) {
        for (let z = 0; z < counts.z; z++) {
          const idx = current.index(x, y, z);
          let maxVal = current.data[idx];

          for (const [dx, dy, dz] of neighbors) {
            const nx = x + dx, ny = y + dy, nz = z + dz;
            if (!current.inBounds(nx, ny, nz)) continue;
            const nidx = current.index(nx, ny, nz);
            maxVal = Math.max(maxVal, current.data[nidx]);
          }

          // Add cell size (shrink = more positive)
          next.data[idx] = maxVal + cellSize;
        }
      }
    }

    current = next;
  }

  return current;
}

/**
 * Morphological closing: Dilation followed by Erosion
 * Fills small gaps while preserving overall shape
 */
export function closeSDF(
  grid: VoxelGrid,
  iterations: number,
  kernel: KernelType = '6-connected',
): VoxelGrid {
  const dilated = dilateSDF(grid, iterations, kernel);
  return erodeSDF(dilated, iterations, kernel);
}

/**
 * Morphological closing without the intermediate grid copies of `closeSDF` —
 * matters once jaw grids run at ~10⁸ voxels, where each clone is hundreds of
 * MB. The 26-connected kernel runs as separable in-place passes; other kernels
 * fall back to the allocating path and copy the result back.
 */
export function closeSDFInPlace(
  grid: VoxelGrid,
  iterations: number,
  kernel: KernelType = '26-connected',
): void {
  if (iterations <= 0) return;
  if (kernel === '26-connected') {
    boxExtremeSDFInPlace(grid, iterations, true);
    boxExtremeSDFInPlace(grid, iterations, false);
    return;
  }
  grid.data.set(closeSDF(grid, iterations, kernel).data);
}

/**
 * Hollow-shell transform of a RAW signed distance field, in place:
 * sd → max(sd, −sd − slabMm). Material is the slab sd ∈ [−slabMm, 0] behind
 * the surface; everything deeper reads positive again (the interior cavity).
 * Splitting this out of the SDF generation (instead of `sheetSlabMm`) lets
 * solid-level CSG passes — the interproximal carve — edit the SOLID first, so
 * their cut faces get walls from the slab like every other surface.
 */
export function applySheetSlab(grid: VoxelGrid, slabMm: number): void {
  const data = grid.data;
  for (let i = 0; i < data.length; i++) {
    const behind = -data[i] - slabMm;
    if (behind > data[i]) data[i] = behind;
  }
}

/**
 * Morphological opening: Erosion followed by Dilation
 * Removes small protrusions while preserving overall shape
 */
export function openSDF(
  grid: VoxelGrid,
  iterations: number,
  kernel: KernelType = '6-connected',
): VoxelGrid {
  const eroded = erodeSDF(grid, iterations, kernel);
  return dilateSDF(eroded, iterations, kernel);
}

/**
 * Boolean union of two SDFs: min(sdf1, sdf2)
 */
export function unionSDF(a: VoxelGrid, b: VoxelGrid): VoxelGrid {
  const result = new VoxelGrid(a.info);
  for (let i = 0; i < a.data.length; i++) {
    result.data[i] = Math.min(a.data[i], b.data[i]);
  }
  return result;
}

/**
 * Boolean intersection of two SDFs: max(sdf1, sdf2)
 */
export function intersectSDF(a: VoxelGrid, b: VoxelGrid): VoxelGrid {
  const result = new VoxelGrid(a.info);
  for (let i = 0; i < a.data.length; i++) {
    result.data[i] = Math.max(a.data[i], b.data[i]);
  }
  return result;
}

/**
 * Boolean subtraction: max(sdf1, -sdf2)
 */
export function subtractSDF(a: VoxelGrid, b: VoxelGrid): VoxelGrid {
  const result = new VoxelGrid(a.info);
  for (let i = 0; i < a.data.length; i++) {
    result.data[i] = Math.max(a.data[i], -b.data[i]);
  }
  return result;
}

/**
 * Insertion-axis undercut blockout.
 *
 * Pulls every voxel that is "more outside" than something *above* it (along +Z,
 * the insertion direction in the aligner-aligned workspace) toward the most-
 * inside SDF value seen so far in the same XY column. The result is a volume
 * whose cross-section is monotonically non-shrinking from the bottom up — i.e.
 * a shell extracted from this SDF has no overhangs along +Z and is removable.
 *
 * The blend amount is controlled by `strength` in [0, 1]:
 *   strength = 0 — no change
 *   strength = 1 — perfect blockout (column = running min top-down)
 *
 * Run AFTER morphological close (so the smoothed surface is the source) and
 * BEFORE trim-field application (so trim still wins under the cut).
 */
export function blockoutAlongInsertion(grid: VoxelGrid, strength: number): VoxelGrid {
  const result = grid.clone();
  if (strength <= 0) return result;
  const s = Math.min(1, strength);
  const { counts } = grid.info;

  for (let x = 0; x < counts.x; x++) {
    for (let y = 0; y < counts.y; y++) {
      let minSoFar = Number.POSITIVE_INFINITY;
      for (let z = counts.z - 1; z >= 0; z--) {
        const idx = result.index(x, y, z);
        const v = result.data[idx];
        if (v <= minSoFar) {
          minSoFar = v;
        } else {
          result.data[idx] = v * (1 - s) + minSoFar * s;
        }
      }
    }
  }

  return result;
}

/**
 * Offset SDF by a distance (positive = shrink, negative = expand)
 */
export function offsetSDF(grid: VoxelGrid, distance: number): VoxelGrid {
  const result = new VoxelGrid(grid.info);
  for (let i = 0; i < grid.data.length; i++) {
    result.data[i] = grid.data[i] + distance;
  }
  return result;
}

/**
 * Apply box blur (smoothing) to a grid
 */
export function blur3D(
  grid: VoxelGrid,
  radius: number = 1,
): VoxelGrid {
  const { counts } = grid.info;
  const result = new VoxelGrid(grid.info);
  const r = Math.max(1, Math.floor(radius));

  for (let x = 0; x < counts.x; x++) {
    for (let y = 0; y < counts.y; y++) {
      for (let z = 0; z < counts.z; z++) {
        let sum = 0;
        let count = 0;

        for (let dx = -r; dx <= r; dx++) {
          const nx = x + dx;
          if (nx < 0 || nx >= counts.x) continue;
          for (let dy = -r; dy <= r; dy++) {
            const ny = y + dy;
            if (ny < 0 || ny >= counts.y) continue;
            for (let dz = -r; dz <= r; dz++) {
              const nz = z + dz;
              if (nz < 0 || nz >= counts.z) continue;
              const v = grid.get(nx, ny, nz);
              if (Number.isFinite(v)) {
                sum += v;
                count++;
              }
            }
          }
        }

        const idx = grid.index(x, y, z);
        result.data[idx] = count > 0 ? sum / count : grid.data[idx];
      }
    }
  }

  return result;
}

export interface PruneComponentsOptions {
  /** Components smaller than this absolute voxel count are removed. */
  minVoxels?: number;
  /** Components smaller than this fraction of the largest component are removed. */
  minFractionOfLargest?: number;
}

export interface PruneComponentsResult {
  grid: VoxelGrid;
  componentCount: number;
  removedComponents: number;
  removedVoxels: number;
  largestVoxels: number;
}

/**
 * Remove small floating "inside" (sdf < 0) components, flipping them to outside.
 *
 * Uses 26-connected flood fill to label inside components, then keeps any
 * component that is large in absolute terms OR a large fraction of the biggest
 * component (so a genuinely isolated tooth survives, but stray ambiguous-sign
 * blobs are dropped). Distances of kept voxels are untouched.
 */
export function pruneInsideComponents(
  grid: VoxelGrid,
  options: PruneComponentsOptions = {},
): PruneComponentsResult {
  const minVoxels = options.minVoxels ?? 64;
  const minFractionOfLargest = options.minFractionOfLargest ?? 0.05;

  const result = grid.clone();
  const { total } = result.info;
  const data = result.data;
  const label = new Int32Array(total).fill(-1);
  const stack = new Int32Array(total);
  const sizes: number[] = [];

  let componentCount = 0;
  for (let start = 0; start < total; start++) {
    if (data[start] >= 0 || label[start] !== -1) continue;
    const size = floodInsideComponent(result, data, label, stack, start, componentCount);
    sizes.push(size);
    componentCount += 1;
  }

  const largestVoxels = sizes.reduce((max, s) => Math.max(max, s), 0);
  const threshold = Math.max(minVoxels, Math.floor(largestVoxels * minFractionOfLargest));
  const outsideValue = result.info.cellSize;

  let removedComponents = 0;
  let removedVoxels = 0;
  for (const size of sizes) {
    if (size < threshold) {
      removedComponents += 1;
      removedVoxels += size;
    }
  }

  if (removedComponents > 0) {
    for (let i = 0; i < total; i++) {
      const l = label[i];
      if (l >= 0 && sizes[l] < threshold) {
        data[i] = outsideValue;
      }
    }
  }

  return { grid: result, componentCount, removedComponents, removedVoxels, largestVoxels };
}

/** Flood one 26-connected inside component from `start`; returns its voxel count. */
function floodInsideComponent(
  grid: VoxelGrid,
  data: Float32Array,
  label: Int32Array,
  stack: Int32Array,
  start: number,
  componentLabel: number,
): number {
  const { counts, strides } = grid.info;
  let sp = 0;
  stack[sp++] = start;
  label[start] = componentLabel;
  let size = 0;

  while (sp > 0) {
    const idx = stack[--sp];
    size += 1;
    const x = Math.floor(idx / strides.x);
    const rem = idx % strides.x;
    const y = Math.floor(rem / strides.y);
    const z = rem % strides.y;

    for (const [dx, dy, dz] of NEIGHBORS_26) {
      const nx = x + dx;
      const ny = y + dy;
      const nz = z + dz;
      if (nx < 0 || ny < 0 || nz < 0 || nx >= counts.x || ny >= counts.y || nz >= counts.z) {
        continue;
      }
      const nIdx = idx + dx * strides.x + dy * strides.y + dz * strides.z;
      if (data[nIdx] >= 0 || label[nIdx] !== -1) continue;
      label[nIdx] = componentLabel;
      stack[sp++] = nIdx;
    }
  }

  return size;
}

/**
 * Apply Gaussian-like blur with custom weights
 */
export function gaussianBlur3D(grid: VoxelGrid, sigma: number = 1): VoxelGrid {
  const radius = Math.ceil(sigma * 2);
  const { counts } = grid.info;
  const result = new VoxelGrid(grid.info);

  // Precompute Gaussian weights
  const weights: number[][][] = [];
  let totalWeight = 0;
  for (let dx = -radius; dx <= radius; dx++) {
    weights[dx + radius] = [];
    for (let dy = -radius; dy <= radius; dy++) {
      weights[dx + radius][dy + radius] = [];
      for (let dz = -radius; dz <= radius; dz++) {
        const dist2 = dx * dx + dy * dy + dz * dz;
        const w = Math.exp(-dist2 / (2 * sigma * sigma));
        weights[dx + radius][dy + radius][dz + radius] = w;
        totalWeight += w;
      }
    }
  }

  // Normalize
  for (let dx = -radius; dx <= radius; dx++) {
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dz = -radius; dz <= radius; dz++) {
        weights[dx + radius][dy + radius][dz + radius] /= totalWeight;
      }
    }
  }

  // Apply filter
  for (let x = 0; x < counts.x; x++) {
    for (let y = 0; y < counts.y; y++) {
      for (let z = 0; z < counts.z; z++) {
        let sum = 0;
        let weightSum = 0;

        for (let dx = -radius; dx <= radius; dx++) {
          const nx = x + dx;
          if (nx < 0 || nx >= counts.x) continue;
          for (let dy = -radius; dy <= radius; dy++) {
            const ny = y + dy;
            if (ny < 0 || ny >= counts.y) continue;
            for (let dz = -radius; dz <= radius; dz++) {
              const nz = z + dz;
              if (nz < 0 || nz >= counts.z) continue;
              
              const v = grid.get(nx, ny, nz);
              const w = weights[dx + radius][dy + radius][dz + radius];
              if (Number.isFinite(v)) {
                sum += v * w;
                weightSum += w;
              }
            }
          }
        }

        const idx = grid.index(x, y, z);
        result.data[idx] = weightSum > 0 ? sum / weightSum : grid.data[idx];
      }
    }
  }

  return result;
}

